import React, { useState } from 'react';
import { X, AlertTriangle, Send } from 'lucide-react';
import { cn } from '../../../utils/cn';

// Dispute categories
const DISPUTE_CATEGORIES = [
  { value: 'quality', label: 'Quality Issues', description: 'Work does not meet expected standards' },
  { value: 'incomplete', label: 'Incomplete Work', description: 'Job was not fully completed' },
  { value: 'time', label: 'Time Discrepancy', description: 'Hours reported do not match actual work' },
  { value: 'damage', label: 'Property Damage', description: 'Damage occurred during work' },
  { value: 'other', label: 'Other', description: 'Other issues not listed above' }
];

/**
 * DisputeModal
 *
 * Modal for creating a dispute on a submitted or approved report.
 * Allows selecting a category and providing details.
 */
export default function DisputeModal({
  instance,
  onClose,
  onDispute
}) {
  const [category, setCategory] = useState('quality');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return;

    setSubmitting(true);
    try {
      await onDispute(reason, category);
    } finally {
      setSubmitting(false);
    }
  };

  const job = instance?.job;
  const contractor = instance?.contractor;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="bg-background w-full max-w-lg rounded-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-text-primary">Raise Dispute</h2>
              <p className="text-text-secondary text-sm">Flag an issue with this report</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Job & Contractor Info */}
          <div className="mb-4 p-3 bg-surface rounded-lg">
            <p className="text-text-primary font-medium">{job?.job_name}</p>
            <p className="text-text-secondary text-sm">Submitted by: {contractor?.full_name}</p>
          </div>

          {/* Dispute Category */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Dispute Category <span className="text-red-400">*</span>
            </label>
            <div className="space-y-2">
              {DISPUTE_CATEGORIES.map(cat => (
                <label
                  key={cat.value}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                    category === cat.value
                      ? 'border-orange-500 bg-orange-500/10'
                      : 'border-border bg-surface hover:border-border-light'
                  )}
                >
                  <input
                    type="radio"
                    name="category"
                    value={cat.value}
                    checked={category === cat.value}
                    onChange={(e) => setCategory(e.target.value)}
                    className="sr-only"
                  />
                  <div className={cn(
                    'w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                    category === cat.value ? 'border-orange-500' : 'border-text-secondary'
                  )}>
                    {category === cat.value && (
                      <div className="w-2 h-2 rounded-full bg-orange-500" />
                    )}
                  </div>
                  <div>
                    <p className={cn(
                      'text-sm font-medium',
                      category === cat.value ? 'text-orange-400' : 'text-text-primary'
                    )}>
                      {cat.label}
                    </p>
                    <p className="text-xs text-text-secondary">{cat.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Dispute Details */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Details <span className="text-red-400">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describe the issue in detail..."
              rows={4}
              className="w-full px-4 py-3 bg-surface border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:border-primary resize-none"
              required
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-border hover:bg-surface-light text-text-primary rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!reason.trim() || submitting}
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-600 disabled:bg-primary/50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              <Send size={18} />
              {submitting ? 'Submitting...' : 'Submit Dispute'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
